import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/app-shell';
import { Spinner } from '@/components/ui';

/**
 * Rotas da aplicacao.
 *
 * Explicitas, e nao derivadas da arvore de arquivos: o caminho de cada tela fica
 * visivel num lugar so, e acrescentar uma tela e uma linha aqui em vez de uma
 * convencao de pasta a ser lembrada.
 *
 * Cada pagina entra por `lazy`, e nao por importacao direta: assim cada tela vira
 * um pacote proprio e o primeiro carregamento nao traz as 27. Sem isso o bundle
 * passava de 500 kB, e quem abre o painel pagava pelo DRE que talvez nunca abra.
 *
 * Tudo que nao e `/login` fica dentro do `AppShell`, que redireciona quem nao tem
 * sessao — nenhuma pagina repete essa verificacao.
 */

const LoginPage = lazy(() => import('@/pages/login'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));

const PartnersPage = lazy(() => import('@/pages/partners'));
const PartnerDetailPage = lazy(() => import('@/pages/partner-detail'));
const CustomerGroupsPage = lazy(() => import('@/pages/customer-groups'));
const ItemsPage = lazy(() => import('@/pages/items'));
const ItemDetailPage = lazy(() => import('@/pages/item-detail'));
const CategoriesPage = lazy(() => import('@/pages/categories'));

const PriceListsPage = lazy(() => import('@/pages/price-lists'));
const PriceCheckPage = lazy(() => import('@/pages/price-check'));

const BillingPage = lazy(() => import('@/pages/billing'));
const ContractsPage = lazy(() => import('@/pages/contracts'));
const ContractItemsPage = lazy(() => import('@/pages/contract-items'));
const SalesPage = lazy(() => import('@/pages/sales'));
const EconomicIndexesPage = lazy(() => import('@/pages/economic-indexes'));

const ReceivablesPage = lazy(() => import('@/pages/receivables'));
const PayablesPage = lazy(() => import('@/pages/payables'));
const BankAccountsPage = lazy(() => import('@/pages/bank-accounts'));
const CashFlowPage = lazy(() => import('@/pages/cash-flow'));
const IncomeStatementPage = lazy(() => import('@/pages/income-statement'));

const ChartOfAccountsPage = lazy(() => import('@/pages/chart-of-accounts'));
const FinancialCategoriesPage = lazy(() => import('@/pages/financial-categories'));
const CostCentersPage = lazy(() => import('@/pages/cost-centers'));
const FinancialPoliciesPage = lazy(() => import('@/pages/financial-policies'));

const CompaniesPage = lazy(() => import('@/pages/companies'));
const ChargesPage = lazy(() => import('@/pages/charges'));
const PortalAdminPage = lazy(() => import('@/pages/portal-admin'));

// O portal do cliente (§37) tem casca, sessao e cliente de API proprios: as
// paginas abaixo **nao** passam pelo `AppShell`, e o token delas e assinado com
// outro segredo — um usuario de portal nao alcanca o ERP nem por engano.
const PortalLoginPage = lazy(() => import('@/pages/portal/login'));
const PortalHomePage = lazy(() => import('@/pages/portal/home'));
const PortalFinancePage = lazy(() => import('@/pages/portal/finance'));
const PortalDocumentsPage = lazy(() => import('@/pages/portal/documents'));
const PortalContractsPage = lazy(() => import('@/pages/portal/contracts'));
const PortalRequestsPage = lazy(() => import('@/pages/portal/requests'));
const PortalAccountPage = lazy(() => import('@/pages/portal/account'));
const PortalPasswordPage = lazy(() => import('@/pages/portal/password'));
const ReconciliationPage = lazy(() => import('@/pages/reconciliation'));
const MessagingPage = lazy(() => import('@/pages/messaging'));
const FiscalDocumentsPage = lazy(() => import('@/pages/fiscal-documents'));
const UsersPage = lazy(() => import('@/pages/users'));
const RolesPage = lazy(() => import('@/pages/roles'));
const CustomFieldsPage = lazy(() => import('@/pages/custom-fields'));
const LookupProvidersPage = lazy(() => import('@/pages/lookup-providers'));
const CertificatesPage = lazy(() => import('@/pages/certificates'));
const BankIntegrationsPage = lazy(() => import('@/pages/bank-integrations'));
const ImportsPage = lazy(() => import('@/pages/imports'));
const ApprovalsPage = lazy(() => import('@/pages/approvals'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const PrivacyPage = lazy(() => import('@/pages/privacy'));
const NotificationsPage = lazy(() => import('@/pages/notifications'));
const FiscalClosingPage = lazy(() => import('@/pages/fiscal-closing'));

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<Spinner />}>
            <LoginPage />
          </Suspense>
        }
      />

      {/* Portal do cliente (§37): fora do `AppShell` de proposito. A casca, a
          guarda de sessao e o cliente de API sao outros, e o menu interno nao
          deve sequer existir na arvore de quem entra por aqui. */}
      <Route
        path="/portal/entrar"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalLoginPage />
          </Suspense>
        }
      />
      <Route
        path="/portal/convite"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalLoginPage />
          </Suspense>
        }
      />
      <Route
        path="/portal"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalHomePage />
          </Suspense>
        }
      />
      <Route
        path="/portal/financeiro"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalFinancePage />
          </Suspense>
        }
      />
      <Route
        path="/portal/notas"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalDocumentsPage />
          </Suspense>
        }
      />
      <Route
        path="/portal/contratos"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalContractsPage />
          </Suspense>
        }
      />
      <Route
        path="/portal/solicitacoes"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalRequestsPage />
          </Suspense>
        }
      />
      <Route
        path="/portal/conta"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalAccountPage />
          </Suspense>
        }
      />
      <Route
        path="/portal/senha"
        element={
          <Suspense fallback={<Spinner />}>
            <PortalPasswordPage />
          </Suspense>
        }
      />

      <Route
        path="/*"
        element={
          <AppShell>
            {/* O fallback fica dentro do shell: o menu permanece na tela enquanto
                a pagina carrega, em vez de a interface inteira piscar. */}
            <Suspense fallback={<Spinner />}>
              <Routes>
                <Route path="/" element={<DashboardPage />} />

                {/* Cadastros */}
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:id" element={<PartnerDetailPage />} />
                <Route path="/customer-groups" element={<CustomerGroupsPage />} />
                <Route path="/items" element={<ItemsPage />} />
                <Route path="/items/:id" element={<ItemDetailPage />} />
                <Route path="/categories" element={<CategoriesPage />} />

                {/* Precos */}
                <Route path="/price-lists" element={<PriceListsPage />} />
                <Route path="/price-check" element={<PriceCheckPage />} />

                {/* Faturamento */}
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/contracts" element={<ContractsPage />} />
                <Route path="/contract-items" element={<ContractItemsPage />} />
                <Route path="/sales" element={<SalesPage />} />
                <Route path="/economic-indexes" element={<EconomicIndexesPage />} />

                {/* Financeiro */}
                <Route path="/receivables" element={<ReceivablesPage />} />
                <Route path="/payables" element={<PayablesPage />} />
                <Route path="/bank-accounts" element={<BankAccountsPage />} />
                <Route path="/cash-flow" element={<CashFlowPage />} />
                <Route path="/income-statement" element={<IncomeStatementPage />} />

                {/* Estrutura financeira */}
                <Route path="/chart-of-accounts" element={<ChartOfAccountsPage />} />
                <Route path="/financial-categories" element={<FinancialCategoriesPage />} />
                <Route path="/cost-centers" element={<CostCentersPage />} />
                <Route path="/financial-policies" element={<FinancialPoliciesPage />} />

                {/* Configuracoes */}
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/charges" element={<ChargesPage />} />
                <Route path="/portal-admin" element={<PortalAdminPage />} />
                <Route path="/reconciliation" element={<ReconciliationPage />} />
                <Route path="/messaging" element={<MessagingPage />} />
                <Route path="/fiscal-documents" element={<FiscalDocumentsPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/custom-fields" element={<CustomFieldsPage />} />
                <Route path="/lookup-providers" element={<LookupProvidersPage />} />
                <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/bank-integrations" element={<BankIntegrationsPage />} />
                <Route path="/imports" element={<ImportsPage />} />
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/fiscal-closing" element={<FiscalClosingPage />} />

                {/* Caminho desconhecido volta ao painel, em vez de tela branca. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AppShell>
        }
      />
    </Routes>
  );
}
